import { useRef, useState } from 'react';

import { Button, Card, useToast } from '../../components/index.ts';
import { applyRestore, exportJson, previewRestore } from './backup.ts';
import type { RestorePreview } from './backup.ts';

/**
 * Settings → Data. Export, restore, and a plain statement of where a trainee's
 * work actually lives.
 *
 * This is the control for hazard H1 — losing a logbook — and the reason it is
 * two obvious buttons rather than anything cleverer. A backup feature that
 * needs explaining is a backup feature nobody uses.
 */
export function DataSection({
  userId,
  displayName,
}: {
  userId: string | null;
  displayName: string;
}) {
  const { showToast } = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<RestorePreview | null>(null);

  async function run(work: () => Promise<string>): Promise<void> {
    setBusy(true);
    try {
      showToast({ message: `${await work()} downloaded.`, tone: 'success' });
    } catch {
      showToast({ message: 'That export failed. Try again.', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  async function choose(file: File | undefined): Promise<void> {
    if (file === undefined || userId === null) return;

    setBusy(true);
    try {
      const result = await previewRestore(userId, file);
      if (result.ok) setPreview(result.preview);
      else showToast({ message: result.reason, tone: 'danger' });
    } finally {
      setBusy(false);
      // Cleared so choosing the same file twice still fires a change event.
      if (fileInput.current !== null) fileInput.current.value = '';
    }
  }

  async function confirmRestore(): Promise<void> {
    if (preview === null || userId === null) return;

    setBusy(true);
    try {
      const written = await applyRestore(userId, preview.plan);
      const skipped =
        preview.plan.skipped.cases +
        preview.plan.skipped.procedures +
        preview.plan.skipped.edtEntries +
        preview.plan.skipped.anaestheticCases;

      showToast({
        message:
          written === 0
            ? 'Nothing to restore — every entry in that file is already here.'
            : `Restored ${String(written)} ${written === 1 ? 'entry' : 'entries'}${skipped === 0 ? '' : `, skipped ${String(skipped)} already here`}.`,
        tone: 'success',
      });
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  const importing =
    preview === null
      ? 0
      : preview.plan.toImport.cases.length +
        preview.plan.toImport.procedures.length +
        preview.plan.toImport.edtEntries.length +
        preview.plan.toImport.anaestheticCases.length;
  const skipped =
    preview === null
      ? 0
      : preview.plan.skipped.cases +
        preview.plan.skipped.procedures +
        preview.plan.skipped.edtEntries +
        preview.plan.skipped.anaestheticCases;

  return (
    <Card title="Your data">
      <p className="text-sm text-muted">
        Your entries are on this device and, once backed up, on the server. An export is a third
        copy that belongs to you and does not depend on ICULogbook existing.
      </p>

      <div className="flex flex-wrap gap-2 pt-4">
        <Button
          variant="secondary"
          loading={busy}
          disabled={userId === null}
          onClick={() => void run(() => exportJson(userId ?? '', displayName))}
        >
          Export everything (JSON)
        </Button>
      </div>

      <p className="pt-3 text-xs text-faint">
        This is the backup, and the only file Restore can read. It holds everything, including
        entries you have deleted. For a spreadsheet or a PDF of a chosen period, use the ARCP
        summary on your dashboard.
      </p>

      <div className="mt-5 border-t border-line pt-4">
        <h3 className="text-sm font-medium text-text">Restore from a JSON export</h3>
        <p className="pt-1 text-xs text-faint">
          Entries already here are left exactly as they are. Nothing is overwritten and nothing is
          duplicated.
        </p>

        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          onChange={(event) => void choose(event.target.files?.[0])}
        />

        <div className="pt-3">
          <Button variant="secondary" loading={busy} onClick={() => fileInput.current?.click()}>
            Choose a file
          </Button>
        </div>

        {preview === null ? null : (
          <div className="mt-4 rounded-md border border-line bg-surface-raised p-4">
            <p className="text-sm text-text">
              {preview.displayName === '' ? 'An export' : `${preview.displayName}’s export`}, taken{' '}
              {new Date(preview.exportedAt).toLocaleDateString('en-GB')}.
            </p>
            <ul className="flex flex-col gap-1 pt-2 text-sm text-muted">
              <li>{importing === 0 ? 'No entries' : `${String(importing)} entries`} to restore</li>
              {skipped === 0 ? null : (
                <li>{String(skipped)} already here, which will be left alone</li>
              )}
              {preview.plan.rejected.length === 0 ? null : (
                <li className="text-danger">
                  {String(preview.plan.rejected.length)} could not be read and will be skipped
                </li>
              )}
            </ul>

            <div className="flex flex-wrap gap-2 pt-3">
              <Button variant="primary" loading={busy} onClick={() => void confirmRestore()}>
                Restore
              </Button>
              <Button variant="ghost" onClick={() => setPreview(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
